/**
 * Crew Repository
 * COMPLETE implementation for Personnel, Certifications, Hours, Training
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Personnel, CrewCertification, WorkingHours, Training, TrainingRecord } from '../../../types/database';

export class PersonnelRepository extends BaseRepository<Personnel> {
  constructor() {
    super(db.personnel, 'personnel');
  }

  async findByStatus(status: Personnel['status']): Promise<Personnel[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findByRank(rank: string): Promise<Personnel[]> {
    return await this.table.where('rank').equals(rank).toArray();
  }

  async findByDepartment(department: Personnel['department']): Promise<Personnel[]> {
    return await this.table.where('department').equals(department).toArray();
  }

  async findByNationality(nationality: string): Promise<Personnel[]> {
    return await this.table.where('nationality').equals(nationality).toArray();
  }

  async findByShip(shipId: string): Promise<Personnel[]> {
    return await this.table.where('vesselId').equals(shipId).toArray();
  }

  async getByOrganization(organizationId: string): Promise<Personnel[]> {
    return await this.table.where('organizationId').equals(organizationId).toArray();
  }

  async findByOrganization(organizationId: string): Promise<{ data: Personnel[] }> {
    const data = await this.table.where('organizationId').equals(organizationId).toArray();
    return { data };
  }

  async getActive(): Promise<Personnel[]> {
    return await this.findByStatus('active');
  }

  async search(searchTerm: string): Promise<Personnel[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(p =>
      p.firstName.toLowerCase().includes(term) ||
      p.lastName.toLowerCase().includes(term) ||
      p.employeeNumber.toLowerCase().includes(term) ||
      p.rank.toLowerCase().includes(term)
    ).toArray();
  }
}

export class CrewCertificationRepository extends BaseRepository<CrewCertification> {
  constructor() {
    super(db.crewCertifications, 'crewCertifications');
  }

  async findByPersonnel(personnelId: string): Promise<CrewCertification[]> {
    return await this.table.where('personnelId').equals(personnelId).toArray();
  }

  async findExpiring(days: number): Promise<CrewCertification[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table
      .where('expiryDate')
      .between(now, futureDate, true, true)
      .toArray();
  }

  async findExpired(): Promise<CrewCertification[]> {
    const now = new Date();
    return await this.table.where('expiryDate').below(now).toArray();
  }

  async findByStatus(status: CrewCertification['status']): Promise<CrewCertification[]> {
    return await this.table.where('status').equals(status).toArray();
  }
}

export class WorkingHoursRepository extends BaseRepository<WorkingHours> {
  constructor() {
    super(db.workingHours, 'workingHours');
  }

  async findByPersonnel(personnelId: string): Promise<WorkingHours[]> {
    return await this.table.where('personnelId').equals(personnelId).toArray();
  }

  async findByDateRange(personnelId: string, startDate: Date, endDate: Date): Promise<WorkingHours[]> {
    const all = await this.findByPersonnel(personnelId);
    return all.filter(wh => {
      const date = new Date(wh.date);
      return date >= startDate && date <= endDate;
    });
  }

  async getLast7Days(personnelId: string): Promise<WorkingHours[]> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return await this.findByDateRange(personnelId, startDate, endDate);
  }
}

export class TrainingRepository extends BaseRepository<Training> {
  constructor() {
    super(db.trainings, 'trainings');
  }

  async findRequired(): Promise<Training[]> {
    return await this.table.filter(t => t.required).toArray();
  }

  async findByCategory(category: string): Promise<Training[]> {
    return await this.table.where('category').equals(category).toArray();
  }
}

export class TrainingRecordRepository extends BaseRepository<TrainingRecord> {
  constructor() {
    super(db.trainingRecords, 'trainingRecords');
  }

  async findByPersonnel(personnelId: string): Promise<TrainingRecord[]> {
    return await this.table.where('personnelId').equals(personnelId).toArray();
  }

  async findByTraining(trainingId: string): Promise<TrainingRecord[]> {
    return await this.table.where('trainingId').equals(trainingId).toArray();
  }

  async findExpired(): Promise<TrainingRecord[]> {
    const now = new Date();
    return await this.table.filter(tr =>
      tr.expiryDate !== undefined && new Date(tr.expiryDate) < now
    ).toArray();
  }
}

export const personnelRepository = new PersonnelRepository();
export const crewRepository = personnelRepository; // Alias for backward compatibility
export const crewCertificationRepository = new CrewCertificationRepository();
export const workingHoursRepository = new WorkingHoursRepository();
export const trainingRepository = new TrainingRepository();
export const trainingRecordRepository = new TrainingRecordRepository();


